import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@material-ui/core/Button';
import { Typography } from '@material-ui/core';
import { t } from 'i18next';
import {
    TextFields,
    RadioButtonChecked,
    CheckBox,
    FormatAlignLeft
} from '@material-ui/icons';

import AddInputField from './AddInputField';
import AddChoiceField from './AddChoiceField';
import { FIELD_TYPES } from '../DynamicForm/consts';

import './AddOrUpdateField.scss';
import { SCHEMA_TYPE } from '../DynamicForm/types';

const FIELD_TYPE_BOXES = [
    {
        name: t('CREATE_OR_UPDATE_FORM_SINGLE_LINE_TEXT'),
        icon: <TextFields />,
        type: FIELD_TYPES.Input,
    },
    {
        name: t('CREATE_OR_UPDATE_FORM_MULTI_LINE_TEXT'),
        icon: <FormatAlignLeft />,
        type: FIELD_TYPES.Multiline,
    },
    {
        name: t('CREATE_OR_UPDATE_FORM_MULTIPLE_CHOICE'),
        icon: <CheckBox />,
        type: FIELD_TYPES.MultipleChoice,
    },
    {
        name: t('CREATE_OR_UPDATE_FORM_RADIO_BUTTONS'),
        icon: <RadioButtonChecked />,
        type: FIELD_TYPES.SingleChoice,
    }
];

export interface FieldData {
    type: SCHEMA_TYPE;
    title: string;
    isRequired: boolean;
    options: string[];
};

interface AddOrUpdateFieldProps {
    data?: FieldData;
    onChange: (_field: any) => void;
    onCancel: () => void;
};

const AddOrUpdateField = ({ data, onChange, onCancel }: AddOrUpdateFieldProps) => {
    const { t } = useTranslation();

    const [newField, setNewField] = useState<any>(null);
    const [selectedFieldType, setSelectedFieldType] = useState<string>(data?.type || FIELD_TYPE_BOXES[0].type);

    const handleSelectFieldType = (type: string) => {
        setNewField(null);
        setSelectedFieldType(type);
    };

    const handleAddField = () => {
        onChange({ ...newField, type: selectedFieldType });
        onCancel();
    };

    const getAddFieldForType = () => {
        switch (selectedFieldType) {
            case FIELD_TYPES.Input:
                return <AddInputField data={data} onChange={setNewField} />;
            case FIELD_TYPES.Multiline:
                return <AddInputField data={data} onChange={setNewField} isMultiline={true} />;
            case FIELD_TYPES.MultipleChoice:
                return <AddChoiceField data={data} onChange={setNewField} isMultiChoice={true} />;
            case FIELD_TYPES.SingleChoice:
                return <AddChoiceField data={data} onChange={setNewField} />;
            default:
                return null;
        }
    };

    return (
        <>
            <div className="addFieldFormWrepper">
                <div className="addNewFieldTitle">{t('CREATE_OR_UPDATE_FORM_FIELD_TYPE')}</div>
                <div className="addNewFieldButtons">
                    {FIELD_TYPE_BOXES.map(({ name, icon, type }, index) => (
                        <Button
                            key={index}
                            variant="outlined"
                            className={type === selectedFieldType ? 'selected' : ''}
                            onClick={() => handleSelectFieldType(type)}>
                            {icon}
                            <Typography style={{ marginLeft: '5px' }}>
                                {name}
                            </Typography>
                        </Button>
                    ))}
                </div>
                { getAddFieldForType() }
            </div>
            <div className="addNewFieldActions">
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={onCancel}
                >
                    {t('COMMON_CANCEL')}
                </Button>
                <Button
                    variant="contained"
                    color={newField ? 'primary' : 'inherit'}
                    onClick={handleAddField}
                    disabled={!newField}
                >
                    {data ?
                        t('CREATE_OR_UPDATE_FORM_UPDATE_FIELD')
                        :
                        t('CREATE_OR_UPDATE_FORM_ADD_FIELD')
                    }
                </Button>
            </div>
        </>
    );
};

export default AddOrUpdateField;
